import { useState } from 'react'
import { useForm, SubmitHandler } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { removeFinalSpaces } from 'helper/regex'
import SectionService from 'services/Sector'
import SectorActions from 'actions/Sector'
import { Dispatch } from 'redux'
import { Sector, SectorAction } from 'types/sector'
import { SectorFormData, createSectorSchema } from '../schemas/sectorSchema'
import { useParams } from 'react-router-dom';

interface UseSectorDrawerProps {
    sectorDispatch: Dispatch<SectorAction>
    locationName?: string
    sectorsList: Sector[]
}

export const useSectorDrawer = ({
    sectorDispatch,
    locationName,
    sectorsList,
}: UseSectorDrawerProps) => {
    const { id: locationId } = useParams();
    const [openDrawer, setOpenDrawer] = useState(false)
    const [openModal, setOpenModal] = useState(false)
    const [openTransferModal, setOpenTransferModal] = useState(false)
    const [createNewSector, setCreateNewSector] = useState(false)
    const [currentSectorId, setCurrentSectorId] = useState<string | null>(null)
    const [isCheckingNotifications, setIsCheckingNotifications] = useState(false)
    const [hasNotifications, setHasNotifications] = useState<boolean | null>(null)
    const [originalStatus, setOriginalStatus] = useState<string | null>(null)
    const [toastOpen, setToastOpen] = useState(false)
    const [toastMessage, setToastMessage] = useState('')

    const {
        getValues,
        watch,
        setValue,
        formState,
        reset,
        handleSubmit,
        formState: { errors }
    } = useForm<SectorFormData>({
        resolver: yupResolver(createSectorSchema(currentSectorId, sectorsList, hasNotifications)) as any,
        defaultValues: {
            id: null,
            name: '',
            status: 'true',
            transferSectorId: ''
        },
        mode: 'onChange'
    })

    const setMessageSuccess = (text: string) => {
        setToastMessage(text)
        // Toast aparecerá após o drawer fechar
        setTimeout(() => {
            setToastOpen(true)
            // Toast desaparece após 3 segundos
            setTimeout(() => {
                setToastOpen(false)
            }, 3000)
        }, 300) // Pequeno delay para garantir que drawer fechou
    }

    const getSuccessMessage = (isCreate: boolean, originalStatus: string | null, newStatus: string, sectorName: string) => {
        if (isCreate) {
            return `Setor "${sectorName}" adicionado com sucesso!`
        }
        
        // Para edições, verificar se mudou o status
        if (originalStatus && originalStatus !== newStatus) {
            if (newStatus === 'true') {
                return `Setor "${sectorName}" ativado com sucesso!`
            } else {
                return `Setor "${sectorName}" inativado com sucesso!`
            }
        }
        
        // Para outras edições
        return `Setor "${sectorName}" editado com sucesso!`
    }

    const checkSectorNotifications = async (sectorId: string) => {
        setIsCheckingNotifications(true)
        try {
            const response = await SectionService.countNotificationsBySector(sectorId)
            const result = response?.data?.result
            
            // Se result for array vazio, não tem notificações
            if (Array.isArray(result) && result.length === 0) {
                setHasNotifications(false)
                return
            }
            
            // Coletar todos os status de todas as notificações
            const statusValues = result.flatMap((item: any) => {
                const stateField = item.groupby_fields?.find((field: any) => field.field === 'u_state')
                return stateField ? [stateField.value] : []
            })

            // Se não há status ou todos os status são 5 ou 6, não há notificações em aberto
            const hasNotifs = statusValues.length > 0 && !statusValues.every((status: string) => status === '5' || status === '6')

            setHasNotifications(hasNotifs)
        } catch (error) {
            console.error('Erro ao verificar notificações:', error)
            setHasNotifications(false)
        } finally {
            setIsCheckingNotifications(false)
        }
    }

    const handleStatusChange = async (newStatus: string) => {
        const currentStatus = watch('status')
        
        // Se está mudando de ativo para inativo e é uma edição
        if (currentStatus === 'true' && newStatus === 'false' && !createNewSector && currentSectorId) {
            setValue('status', newStatus, { shouldDirty: true, shouldValidate: true })
            
            // Buscar todos os setores da localização para ter opções de transferência
            SectorActions.getAllSectorsByLocationId(
                sectorDispatch,
                locationId || '',
                '', // sem busca
                '', // sem filtro de status
                { field: 'u_name', order: 'ORDERBY' }
            )
            
            await checkSectorNotifications(currentSectorId)
        } else {
            setValue('status', newStatus, { shouldDirty: true, shouldValidate: true })
            setHasNotifications(null)
            setValue('transferSectorId', '', { shouldDirty: true, shouldValidate: true })
        }
    }

    const getAvailableSectors = () => {
        return sectorsList.filter(sector => 
            sector.u_active === 'true' && 
            sector.sys_id !== currentSectorId
        )
    }

    const createSector = () => {
        setCurrentSectorId(null)
        setHasNotifications(null)
        setOriginalStatus(null)
        reset({
            id: null,
            name: '',
            status: 'true',
            transferSectorId: ''
        })
        setOpenDrawer(true)
        setCreateNewSector(true)
    }

    const handleEdit = (sector: Sector) => {
        setCurrentSectorId(sector.sys_id)
        setHasNotifications(null)
        setOriginalStatus(sector.u_active) // Salvar status original
        reset({
            id: sector.sys_id,
            name: sector.u_name,
            status: sector.u_active,
            transferSectorId: ''
        })
        setOpenDrawer(true)
        setCreateNewSector(false)
    }

    const saveSector: SubmitHandler<SectorFormData> = (data) => {
        if (createNewSector) {
            handleSaveSectorWithData(data)
        } else {
            // A validação do transferSectorId é feita pelo schema yup quando status é 'false'
            
            // Mostrar modal de confirmação quando:
            // 1. Mudou o nome do setor -> Modal de nome
            // 2. Mudou o status para inativo -> Modal de transferência
            if (formState.dirtyFields.name) {
                setOpenModal(true)
            } else if (formState.dirtyFields.status && data.status === 'false') {
                setOpenTransferModal(true)
            } else {
                if (formState.dirtyFields.status) {
                    handleSaveSectorWithData(data)
                } else {
                    setOpenDrawer(false)
                }
            }
        }
    }

    const updateSector = () => {
        const data = getValues()
        handleSaveSectorWithData(data)
    }

    const confirmTransferSector = () => {
        const data = getValues()
        setOpenTransferModal(false)
        handleSaveSectorWithData(data)
    }

    const handleSaveSectorWithData = (data: SectorFormData) => {
        const newSector: Sector = {
            u_name: removeFinalSpaces(data.name),
            u_active: data.status,
            u_location: locationName
        }
        
        // Dados para transferência de notificações (quando inativando)
        const transferData = data.status === 'false' && data.transferSectorId ? {
            fromSectorId: data.id,
            toSectorId: data.transferSectorId,
            hasNotifications: hasNotifications
        } : null
        
        console.log('Payload pronto para backend:', {
            sector: newSector,
            transfer: transferData,
            locationId: locationId
        })

        if (!data.id) {
            SectionService.createNewSector(newSector)
                .then((response: any) => {
                    const sector = response?.data?.result;
                    if (sector) {
                        setOpenDrawer(false);
                        SectorActions.createNewSector(sectorDispatch, {
                            ...sector,
                            u_active: sector.u_active === 'verdadeiro' ? 'true' : 'false'
                        });
                        SectorActions.getAllSectorsByLocationId(sectorDispatch,locationId, "", "");
                        const successMessage = getSuccessMessage(true, null, data.status, newSector.u_name);
                        setMessageSuccess(successMessage);
                    }
                })
                .catch((error) => {
                    console.error('Erro ao criar setor:', error)
                    setOpenDrawer(false);
                })
        } else {
            // Lógica para determinar qual endpoint chamar baseado no que foi alterado
            let updateMethod;
            
            if (formState.dirtyFields.status && data.status === 'false') {
                // Status foi alterado para inativo - usar update_all_notification_and_sectors
                // Se também mudou o nome, primeiro atualiza o nome
                if (formState.dirtyFields.name) {
                    // Primeiro atualiza o nome
                    SectionService.updateSectorBySysId(newSector, data.id!)
                        .then(() => {
                            // Depois atualiza o status com transferência
                            return SectionService.updateSectorWithNotifications(newSector, data.id!, transferData, locationId)
                        })
                        .then((response: any) => {
                            SectorActions.updateSector(sectorDispatch, {
                                sys_id: data.id,
                                u_name: newSector.u_name,
                                u_active: newSector.u_active
                            })
                            
                            setOpenModal(false)
                            setOpenDrawer(false)
                            
                            const successMessage = getSuccessMessage(false, originalStatus, data.status, newSector.u_name)
                            setMessageSuccess(successMessage)
                        })
                        .catch((error) => {
                            console.error('Erro ao atualizar setor:', error)
                        })
                    return; // Sai da função para não executar o código abaixo
                }
                updateMethod = SectionService.updateSectorWithNotifications(newSector, data.id!, transferData, locationId)
            } else {
                // Outros casos (incluindo mudança de nome) - usar update padrão
                updateMethod = SectionService.updateSectorBySysId(newSector, data.id!)
            }
                
            updateMethod
                .then((response: any) => {
                    SectorActions.updateSector(sectorDispatch, {
                        sys_id: data.id,
                        u_name: newSector.u_name,
                        u_active: newSector.u_active
                    })
                    
                    setOpenModal(false)
                    setOpenDrawer(false)
                    
                    const successMessage = getSuccessMessage(false, originalStatus, data.status, newSector.u_name)
                    setMessageSuccess(successMessage)
                })
                .catch((error) => {
                    console.error('Erro ao atualizar setor:', error)
                })
        }
    }

    return {
        openDrawer,
        setOpenDrawer,
        openModal,
        setOpenModal,
        openTransferModal,
        setOpenTransferModal,
        createNewSector,
        toastOpen,
        setToastOpen,
        toastMessage,
        getValues,
        watch,
        setValue,
        errors,
        handleSubmit,
        createSector,
        handleEdit,
        saveSector,
        updateSector,
        confirmTransferSector,
        handleStatusChange,
        isCheckingNotifications,
        hasNotifications,
        getAvailableSectors,
    }
}
